// src/oura/merchant/WelcomeCard.tsx
//
// "Here is how this works": the first card on Home while the owner still has
// set-up of their own to do (#4062 MR-FE-10).
//
// It replaces SetupBanner, which said how far set-up had got and nothing
// about what the product is. Nobody explained the arrangement to a first-time
// owner: four steps, three of which have already happened or are about to,
// and the one that is theirs has a button on it.
//
// Shown under the same rule as the banner it replaces -- only while something
// is theirs to finish -- so it leaves by itself and never has to be dismissed.
// It sits under Home's title rather than above it, so every merchant page
// opens with its title in the same place. It is the one merchant card that
// folds: an owner who has read the four steps can put them away, and the
// head still says how far the access step has got.

import { useEffect, useId, useState } from "react";
import { CheckIcon, ChevronDownIcon, UsersIcon } from "lucide-react";
import { cn } from "../lib/cn";
import { FOCUS_RING, PillButton, PillLink, SURFACE, TYPE } from "@oura/ui";

/** The fold, kept between visits. Browser storage, so it is per person per device. */
const FOLD_KEY = "oura.welcome.folded";

function readFolded(): boolean {
    try {
        // Folded unless they opened it: the steps are a press away, and the
        // page under it is what they came for (15 Oct 2026).
        return window.localStorage.getItem(FOLD_KEY) !== "0";
    } catch {
        // Private windows and blocked site data throw on read: folded, the
        // same as a first visit.
        return true;
    }
}
import { ouraHref, OURA_TEAM_ACCESS_PATH } from "@oura/nav";
import { setupPart, useMySetup } from "../hooks/useMySetup";
import { startMerchantTour } from "./Tour";
import { ONE_SETUP_UI } from "../onboarding/oneSetup";

interface Step {
    title: string;
    done: boolean;
    /** How far through this step they are, when the step has parts. */
    note?: string;
    action?: { label: string; to?: string; href?: string };
}

export function WelcomeCard() {
    const read = useMySetup();
    const setup = setupPart(read);
    // The questions about their business (#4133): ticked once answered, and a
    // way to them while not. An older backend sends no form: the step reads as before.
    const form = read.status === "ready" ? read.setup.form ?? null : null;
    const [folded, setFolded] = useState(true);
    const listId = useId();

    useEffect(() => {
        setFolded(readFolded());
    }, []);

    const fold = (next: boolean) => {
        setFolded(next);
        try {
            window.localStorage.setItem(FOLD_KEY, next ? "1" : "0");
        } catch {
            // The fold is a convenience; losing it costs a press.
        }
    };
    // Not known, or nothing left that is theirs: the card is a nudge, and a
    // nudge about nothing is noise.
    if (!setup.known || setup.mine === 0) return null;

    // THE ACCESS STEP HAS PARTS, and the card has to say the same thing about
    // them in both states. Folded it reported the access count while the open
    // list showed the step as simply not done, so "3 of 4 done" sat above a
    // step offering to start what was mostly finished (Jack, #4082). One
    // sentence now, from one pair of numbers.
    const given = setup.done;
    const needed = setup.done + setup.mine;
    const accessNote = `${given} of ${needed} given`;

    // One setup: the questions and the access are one step on one page.
    const oneSetup = ONE_SETUP_UI;
    const setupHref = `${ouraHref("decisions", { role: "oura-client" })}?tab=setup`;
    const steps: Step[] = oneSetup ? [
        { title: "Sign in", done: true },
        {
            title: "Tell us about your business and give us access",
            // Never done while shown: the card is gone once nothing is left (`setup.mine === 0` above).
            done: false,
            note: setup.mine === 1 ? "1 thing left" : `${setup.mine} things left`,
            action: { label: given > 0 ? "Continue setup" : "Start setup", to: setupHref },
        },
        { title: "We do the work", done: false },
    ] : [
        { title: "Sign in", done: true },
        {
            title: "Give us access to your Google profile and website",
            // Never done while this card is on screen -- it only renders
            // while something is still theirs -- but how far through shows.
            done: false,
            note: accessNote,
            action: {
                label: given > 0 ? "Continue setup" : "Start setup",
                // The Setup tab inside To do, where set-up lives in the five-item rail.
                to: `${ouraHref("decisions", { role: "oura-client" })}?tab=setup`,
            },
        },
        {
            title: "Answer a few questions about your business",
            done: Boolean(form?.answeredAt),
            action: !form?.answeredAt && form?.link ? { label: "Answer the questions", href: form.link } : undefined,
        },
        { title: "We do the work", done: false },
    ];

    return (
        <section data-tour="welcome" className={cn(SURFACE.card, "p-4 sm:p-5")}>
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="m-0 min-w-0">
                    <button
                        type="button"
                        onClick={() => fold(!folded)}
                        aria-expanded={!folded}
                        aria-controls={listId}
                        className={cn("flex min-w-0 items-center gap-2 rounded text-left text-gray-900", FOCUS_RING)}
                    >
                        <span className={TYPE.tt}>Here is how this works</span>
                        <ChevronDownIcon
                            aria-hidden
                            size={16}
                            className={cn("shrink-0 text-gray-400 transition-transform duration-200 motion-reduce:transition-none", folded && "-rotate-90")}
                        />
                    </button>
                </h2>
                {/* Wraps on a phone: the row ran off the right edge at 390px (audit, 25 Oct). */}
                <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
                    {/* One setup: the card across the top already names the step, so no third count here (audit, 24 Oct 2026). */}
                    {folded && !oneSetup && <span className={cn(TYPE.tc, "tabular-nums text-gray-500 max-sm:basis-full")}>{`Access: ${accessNote}`}</span>}
                    <PillButton onClick={startMerchantTour}>Show me around</PillButton>
                    {/* Inviting colleagues belongs with getting started: here
                        while the card is, then beside "Last checked" on Home's
                        head once set-up is done and the card has gone. */}
                    <PillLink to={OURA_TEAM_ACCESS_PATH} icon={UsersIcon}>
                        Invite your team
                    </PillLink>
                </div>
            </div>

            <ol id={listId} hidden={folded} className="list-plain m-0 mt-3 p-0">
                {steps.map((step, i) => (
                    <li key={step.title} className="flex min-h-10 items-center gap-3 border-t border-gray-100 py-1.5 first:border-t-0">
                        <span
                            aria-hidden
                            className={cn(
                                "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
                                step.done ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500",
                            )}
                        >
                            {step.done ? <CheckIcon aria-hidden size={13} strokeWidth={2.5} /> : i + 1}
                        </span>
                        <span className={cn(TYPE.tb, "min-w-0 flex-1", step.done ? "text-gray-500" : "text-gray-900")}>
                            {step.title}
                            {step.done && <span className="sr-only"> (done)</span>}
                            {step.note && <span className={cn(TYPE.tc, "mt-0.5 block tabular-nums text-gray-500")}>{step.note}</span>}
                        </span>
                        {step.action && (
                            step.action.href ? (
                                <PillLink variant="primary" href={step.action.href} target="_blank" rel="noopener noreferrer">
                                    {step.action.label}
                                </PillLink>
                            ) : (
                                <PillLink variant="primary" to={step.action.to}>
                                    {step.action.label}
                                </PillLink>
                            )
                        )}
                    </li>
                ))}
            </ol>
        </section>
    );
}
